import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export function cn(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';

const TONE_SOFT: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-text border-border',
  accent: 'bg-accent-soft text-text border-accent/40',
  ok: 'bg-ok-soft text-ok border-ok/40',
  warn: 'bg-warn-soft text-warn border-warn/40',
  danger: 'bg-danger-soft text-danger border-danger/40',
  info: 'bg-info-soft text-info border-info/40',
};

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ok';
type Size = 'sm' | 'md' | 'lg' | 'xl';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent-fill text-accent-fg border-transparent hover:brightness-110 active:brightness-95',
  secondary: 'bg-surface-2 text-text border-border hover:bg-surface hover:border-muted/60',
  ghost: 'bg-transparent text-text border-transparent hover:bg-surface-2',
  danger: 'bg-danger text-white dark:text-black border-transparent hover:brightness-110',
  ok: 'bg-ok text-white dark:text-black border-transparent hover:brightness-110',
};

const SIZE: Record<Size, string> = {
  sm: 'min-h-9 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-base gap-2',
  lg: 'min-h-13 px-5 text-lg gap-2',
  xl: 'min-h-16 px-6 text-xl gap-3',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

export function buttonClass(variant: Variant = 'secondary', size: Size = 'md', block?: boolean, className?: string): string {
  return cn(
    'inline-flex items-center justify-center rounded-xl border font-semibold transition select-none',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    VARIANT[variant],
    SIZE[size],
    block && 'w-full',
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', busy, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={buttonClass(variant, size, block, className)}
      {...rest}
    >
      {busy ? <Loader2 className="size-5 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export function IconButton({ label, children, className, size = 'md', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; size?: 'sm' | 'md' | 'lg' }) {
  const dims = size === 'sm' ? 'size-9' : size === 'lg' ? 'size-14' : 'size-11';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn('inline-flex items-center justify-center rounded-xl border border-transparent text-text hover:bg-surface-2 disabled:opacity-40', dims, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('rounded-2xl border border-border bg-surface p-4 sm:p-5 print-plain', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ children, action, className }: { children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-3 flex items-center justify-between gap-3', className)}>
      <h2 className="text-lg font-semibold leading-tight">{children}</h2>
      {action}
    </div>
  );
}

export function Badge({ tone = 'neutral', children, className, title }: { tone?: Tone; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap', TONE_SOFT[tone], className)}>
      {children}
    </span>
  );
}

export function Notice({ tone = 'info', title, children, icon, className, action }: { tone?: Tone; title?: ReactNode; children?: ReactNode; icon?: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <div role={tone === 'danger' ? 'alert' : 'note'} className={cn('flex flex-wrap gap-3 rounded-xl border p-3 sm:p-4', TONE_SOFT[tone], className)}>
      <div className="flex min-w-0 flex-[1_1_16rem] gap-3">
        {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
        <div className="min-w-0 flex-1 text-sm leading-relaxed">
          {title && <p className="font-semibold text-base">{title}</p>}
          {children && <div className={cn(title ? 'mt-1' : '', 'text-text/90')}>{children}</div>}
        </div>
      </div>
      {/* Beside the text when there's room; underneath it on a phone, so the text is never squeezed. */}
      {action && <div className="flex max-w-full flex-wrap items-center gap-2 self-center">{action}</div>}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border px-6 py-10 text-center">
      {icon && <div className="text-muted">{icon}</div>}
      <p className="text-lg font-semibold">{title}</p>
      {children && <div className="max-w-prose text-sm text-muted">{children}</div>}
      {action}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, back, help }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: ReactNode; help?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {back}
        <div className="flex items-center gap-1">
          <h1 className="text-2xl font-bold leading-tight sm:text-3xl">{title}</h1>
          {help}
        </div>
        {subtitle && <div className="mt-1 text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 no-print">{actions}</div>}
    </header>
  );
}

export function Stat({ label, value, sub, tone, href }: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone; href?: string }) {
  const body = (
    <>
      <p className="text-sm font-medium text-muted">{label}</p>
      <p className={cn('mt-1 text-3xl font-bold tabular', tone === 'danger' && 'text-danger', tone === 'warn' && 'text-warn', tone === 'ok' && 'text-ok')}>{value}</p>
      {sub && <p className="mt-1 text-sm text-muted">{sub}</p>}
    </>
  );
  return href ? (
    <a href={href} className="block rounded-2xl border border-border bg-surface p-4 hover:border-accent/60">
      {body}
    </a>
  ) : (
    <div className="rounded-2xl border border-border bg-surface p-4">{body}</div>
  );
}

export function ProgressBar({ value, label, tone = 'accent' }: { value: number; label: string; tone?: 'accent' | 'ok' | 'warn' }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={label} className="h-2.5 w-full overflow-hidden rounded-full bg-surface-2">
      <div className={cn('h-full rounded-full transition-all', tone === 'ok' ? 'bg-ok' : tone === 'warn' ? 'bg-warn' : 'bg-accent-fill')} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-muted">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="min-w-0 break-words font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
